/**
 * What the Billing page may say about a subscription, derived from its state.
 *
 * Deliberately a module of its own, with NO React and no imports beyond the
 * generated types: importing `SystemBilling.tsx` to test these pulls in the whole
 * component tree, including react-router-dom v7 beta, which CRA resolves and Jest
 * 27 does not. Pure decisions belong outside a 1400-line page component anyway.
 */
import type { Subscription } from '../../../api/subscription';

/**
 * Is this subscription on its way out rather than renewing?
 *
 * `current_period_end` means opposite things in the two cases, so one label
 * cannot serve both. The page used to read "Renews / Ends" for everything, which
 * asks the reader to work out which applies — and the answer decides whether they
 * need to act. The owner's own account showed the bad case: a cancelled
 * subscription under a heading that said "Renews".
 *
 * Read STATUS, not linkage: `provider_subscription_id` survives a cancellation by
 * design. Reading linkage instead is what made a cancelled tenant unable to
 * resubscribe at all (fixed 2026-07-30).
 */
export function isEnding(sub: Subscription | null): boolean {
  return !!sub && (sub.status === 'cancelled' || sub.cancel_at_period_end === true);
}

/**
 * Capacity warnings are advice about a plan the tenant is going to keep paying
 * for. On a subscription that is ending, "you are near your branch limit" answers
 * the wrong question: the fix is not to upgrade, it is to resubscribe, which the
 * plan card already offers.
 *
 * True when there is no subscription at all — a free tenant hitting the worker cap
 * is exactly who the banner is for.
 */
export function shouldShowCapacityWarnings(sub: Subscription | null): boolean {
  return !isEnding(sub);
}

/** Statuses the SERVER will accept a cancellation for. Allowlist, not denylist:
 *  a status added later defaults to "do not offer the button", which is the safe
 *  direction (session-discipline §12). Mirrors
 *  `svdy-platform/app/subscription/service.py::cancel`. */
const CANCELLABLE_STATUSES = ['trialing', 'active', 'past_due'] as const;

/**
 * May we offer "Cancel subscription" at all?
 *
 * WHY THIS EXISTS
 * ---------------
 * The page offered it whenever `!cancel_at_period_end && status !== 'cancelled'`.
 * When an organization has no Subscription ROW, the backend synthesizes a free-tier
 * one (`get_current_subscription`, HANDOFF_2026-05-25 Bug 3) carrying
 * `status: 'active'` and `cancel_at_period_end: false` — so BOTH halves of that
 * condition were true and the error-coloured Cancel button rendered for a tenant
 * with nothing to cancel. Production 2026-08-01: **5 of 6 organizations** have no
 * Subscription row, so it was the majority case. Pressing it 500s, because
 * `cancel()` receives the synthetic DICT and reads `sub.status` off it.
 *
 * The owner's own organization is the ONE that did NOT show it (`status` really is
 * `'cancelled'` there), which is why it never appeared in a screenshot — the
 * verifying account being immune to the defect (audit-before-fix Step 0b).
 *
 * READING LINKAGE HERE IS CORRECT, and it is NOT the 2026-07-30 trap. That bug was
 * about deciding whether a CANCELLED tenant may resubscribe, where
 * `provider_subscription_id` survives and so answers the wrong question. This asks
 * something else: can the provider be told to cancel? Linkage IS that question, and
 * the server refuses without it —
 * "Subscription has no provider linkage; free / manual subscriptions can be
 * cancelled by the operator". Asking the same thing the server asks is what stops
 * the UI from offering an action that can only fail.
 */
export function canCancel(sub: Subscription | null): boolean {
  if (!sub) return false;
  if (sub.cancel_at_period_end === true) return false;
  if (!CANCELLABLE_STATUSES.includes(sub.status as typeof CANCELLABLE_STATUSES[number])) {
    return false;
  }
  return !!sub.provider_id && !!sub.provider_subscription_id;
}

/** Whether to offer "Update payment method" at all.
 *
 * Gated on the PROVIDER CUSTOMER existing, not on the subscription being live:
 * a `past_due` tenant is precisely who needs to replace a card, and a tenant
 * whose subscription we just cancelled for non-payment may still want to settle
 * up. What must NOT happen is offering the button to an organization that has
 * never opened a checkout — every Free-plan tenant — because the backend
 * correctly answers null for them and the click would go nowhere.
 *
 * `provider_id` is the same signal the plan card already uses to name the
 * provider, so this cannot disagree with what the page says above it.
 */
export function canUpdatePaymentMethod(sub: Subscription | null): boolean {
  if (!sub) return false;
  return !!sub.provider_id;
}

/** What the plan bills at the interval this subscription is actually on. */
export type PlanAmount =
  | { kind: 'free' }
  /** No fixed price — Enterprise is quoted, not listed. */
  | { kind: 'quoted' }
  | { kind: 'priced'; amount: number; interval: 'month' | 'year'; currency: string };

/**
 * Resolve the headline price, keeping `null` and `0` apart.
 *
 * `price_monthly` arrives as a Decimal serialised to a STRING, and the two
 * non-numeric cases mean opposite things: `null` is Enterprise (quoted), `0` is
 * Free. `parseFloat(null)` is 0, so treating them alike would advertise Enterprise
 * as free — the same trap that once sorted Enterprise next to Free on the pricing
 * page (svdy-ui §12.1).
 */
export function planAmount(
  plan: { price_monthly?: string | number | null; price_yearly?: string | number | null } | null,
  sub: Subscription | null,
): PlanAmount {
  if (!plan) return { kind: 'free' };
  const interval: 'month' | 'year' = sub?.billing_interval === 'year' ? 'year' : 'month';
  const raw = interval === 'year' ? plan.price_yearly : plan.price_monthly;
  if (raw === null || raw === undefined || raw === '') return { kind: 'quoted' };
  const amount = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(amount)) return { kind: 'quoted' };
  if (amount === 0) return { kind: 'free' };
  return {
    kind: 'priced',
    amount,
    interval,
    currency: sub?.billing_currency ?? 'USD',
  };
}

/** The headcount sentence's shape, so the page can pick copy without arithmetic. */
export type SeatSummary =
  | { kind: 'unmetered'; current: number }
  | { kind: 'within'; current: number; included: number; remaining: number }
  | { kind: 'over'; current: number; included: number; extra: number };

/**
 * "How many more people can I add?" is the most frequent reason this page is
 * opened, and the gauge answered it with a percentage — leaving the admin to do
 * the subtraction. This returns the numbers the sentence needs.
 */
export function seatSummary(usage: {
  current_workers?: number | null;
  included_workers?: number | null;
  extra_workers?: number | null;
} | null): SeatSummary | null {
  if (!usage) return null;
  const current = usage.current_workers ?? 0;
  const included = usage.included_workers;
  // Enterprise and legacy rows carry no allotment: there is no "of N" to state.
  if (included === null || included === undefined) return { kind: 'unmetered', current };
  const extra = usage.extra_workers ?? Math.max(0, current - included);
  if (extra > 0) return { kind: 'over', current, included, extra };
  return { kind: 'within', current, included, remaining: Math.max(0, included - current) };
}
